import { createSlice, Slice } from "@reduxjs/toolkit";
import { NavbarLayout } from "./types/navbar.ts";
import { ProgressBarLayout } from "./types/progress-bar.ts";
import { setProgressBarReducer } from "./reducers/progress-bar.reducer.ts";
import { SidebarLayout } from "./types/sidebar.ts";
import { setSidebarReducer } from "./reducers/sidebar.reducer.ts";
import {
  disableLoadingReducer,
  disableSessionLoadingReducer,
  enableLoadingReducer,
  enableSessionLoadingReducer,
  setLoadingReducer,
} from "./reducers/loading.reducer.ts";
import { createDeal } from "../../actions/create-deal.action.ts";
import { submitContacts } from "../../actions/submit-contacts.action.ts";
import { errorReducer } from "./reducers/error.reducer.ts";
import { LayoutError } from "./types/layout-error.ts";

import { verifyOtp } from "../../actions/verify-otp.action.ts";
import { createOtp } from "../../actions/create-otp.action.ts";
import { submitLivingSituation } from "../../actions/submit-living-situation.action.ts";
import { submitIncome } from "../../actions/submit-income.action.ts";
import { submitHiring } from "../../actions/submit-hiring.action.ts";
import { submitFiscalCodeAction } from "../../actions/submit-fiscal-code.action.ts";
import { submitFamily } from "../../actions/submit.family.action.ts";
import { submitEmployment } from "../../actions/submit-employment.action.ts";
import { submitCompanySize } from "../../actions/submit-company-size.action.ts";
import { submitCompanyIndustry } from "../../actions/submit-company-industry.action.ts";
import { submitBirthAction } from "../../actions/submit-birth.action.ts";
import { submitAddress } from "../../actions/submit-address.action.ts";
import { submitPrivacyConsents } from "../../actions/submit-privacy-consents.action.ts";
import { submitVatNumber } from "../../actions/submit-vat-number.action.ts";
import { submitMaritalStatus } from "../../actions/submit-marital-status.action.ts";
import { submitCouponCode } from "../../actions/submit-coupon-code.action.ts";
import { submitPersonalInfo } from "../../actions/submit-personal-info.action.ts";
import { submitAdvData } from "../../actions/submit-adv-data.actions.ts";
import { submitOtherIncome } from "../../actions/submit-other-income.ts";
import { submitGrossIncome } from "../../actions/submit-gross-income.action.ts";
import initStore from "../../actions/init-store.action.ts";
import { calculateOffer } from "../../actions/calculate-offer.action.ts";
import { getOffers } from "../../actions/get-offers.action.ts";
import { getAdvQuestionnaires } from "../../actions/get-adv-questionnaires.action.ts";
import { submitInsurance } from "../../actions/submit-insurance.action.ts";
import { selectOffer } from "../../actions/select-offer.action.ts";
import { editOffer } from "../../actions/edit-offer.action.ts";
import { confirmOffer } from "../../actions/confirm-offer.action.ts";
import { getCoherenceQuestionnaireData } from "../../actions/get-coherence-questionnaire-data.action.ts";
import { submitCoherenceQuestionnaire } from "../../actions/submit-coherence-questionnaire.action.ts";
import { submitIncomeDocumentation } from "../../actions/submit-income-documentation.action.ts";
import { submitIban } from "../../actions/submit-iban.action.ts";
import { submitNextPage } from "../../actions/submit-next-page.ts";
import initStoreReducer from "./reducers/init-store.reducer.ts";
import { submitIbanReducer } from "./reducers/iban.reducer.ts";
import { submitBeneficiary } from "../../actions/submit-beneficiary.action.ts";
import { timeModalReducer } from "./reducers/time-modal.reducer.ts";
import { getDocumentsList } from "../../actions/get-documents-list.action.ts";
import { deselectInsuranceAction } from "../../actions/deselect-insurance.action.ts";

export type LayoutState = {
  hasNavbar: boolean;
  hasProgressBar: boolean;
  hasPartnerLogo: boolean;
  navbar: NavbarLayout;
  sidebar: SidebarLayout;
  progressBar: ProgressBarLayout;
  isLoading: boolean;
  isTimeOfferModalOpen: boolean;
  isSessionLoading: boolean;
  error?: LayoutError;
};

const initialState: LayoutState = {
  hasNavbar: true,
  hasProgressBar: false,
  hasPartnerLogo: false,
  navbar: {
    hasLogo: true,
    hasGradientBackground: false,
    hasBackButton: false,
    hasHelpButton: true,
    hasMenuButton: true,
  },
  sidebar: {
    isOpen: false,
    isDesktop: true,
  },
  progressBar: {
    progress: 0,
    steps: 0,
    completedSteps: 0,
  },
  isLoading: false,
  isTimeOfferModalOpen: false,
  isSessionLoading: false,
};

const layoutSlice: Slice<LayoutState> = createSlice({
  name: "layout",
  initialState,
  reducers: {
    setProgressBar: setProgressBarReducer,
    setSidebar: setSidebarReducer,
    setLoading: setLoadingReducer,
    setError: errorReducer,
    setIsTimeModalOpen: timeModalReducer,
  },
  extraReducers: (builder) => {
    // builder.addCase(setSessionState.fulfilled, disableSessionLoadingReducer);

    builder.addCase(initStore.pending, enableSessionLoadingReducer);
    builder.addCase(initStore.fulfilled, initStoreReducer);
    builder.addCase(initStore.rejected, disableSessionLoadingReducer);
    builder.addCase(submitIban.fulfilled, submitIbanReducer);

    // TODO: Check this handling
    builder.addCase(verifyOtp.fulfilled, () => {});

    builder.addMatcher(
      (action) =>
        [
          createOtp.pending.type,
          verifyOtp.pending.type,
          createDeal.pending.type,
          submitContacts.pending.type,
          submitPrivacyConsents.pending.type,
          submitPersonalInfo.pending.type,
          submitBirthAction.pending.type,
          submitFiscalCodeAction.pending.type,
          submitEmployment.pending.type,
          submitVatNumber.pending.type,
          submitCompanySize.pending.type,
          submitCompanyIndustry.pending.type,
          submitHiring.pending.type,
          submitIncome.pending.type,
          submitOtherIncome.pending.type,
          submitGrossIncome.pending.type,
          submitAddress.pending.type,
          submitMaritalStatus.pending.type,
          submitFamily.pending.type,
          submitLivingSituation.pending.type,
          submitCouponCode.pending.type,
          calculateOffer.pending.type,
          submitInsurance.pending.type,
          getOffers.pending.type,
          getDocumentsList.pending.type,
          selectOffer.pending.type,
          editOffer.pending.type,
          confirmOffer.pending.type,
          submitIban.pending.type,
          submitAdvData.pending.type,
          getAdvQuestionnaires.pending.type,
          getCoherenceQuestionnaireData.pending.type,
          submitCoherenceQuestionnaire.pending.type,
          submitIncomeDocumentation.pending.type,
          submitNextPage.pending.type,
          submitBeneficiary.pending.type,
          deselectInsuranceAction.pending.type,
        ].includes(action.type), // action.type.endsWith('/pending')
      enableLoadingReducer,
    );

    builder.addMatcher(
      (action) =>
        [
          initStore.rejected.type,
          verifyOtp.rejected.type,
          submitPrivacyConsents.rejected.type,
          createDeal.rejected.type,
          submitContacts.rejected.type,
          submitPersonalInfo.rejected.type,
          submitBirthAction.rejected.type,
          submitFiscalCodeAction.rejected.type,
          submitEmployment.rejected.type,
          submitVatNumber.rejected.type,
          submitCompanySize.rejected.type,
          submitCompanyIndustry.rejected.type,
          submitHiring.rejected.type,
          submitIncome.rejected.type,
          submitOtherIncome.rejected.type,
          submitGrossIncome.rejected.type,
          submitAddress.rejected.type,
          submitMaritalStatus.rejected.type,
          submitFamily.rejected.type,
          submitLivingSituation.rejected.type,
          submitCouponCode.rejected.type,
          calculateOffer.rejected.type,
          submitInsurance.rejected.type,
          getOffers.rejected.type,
          getDocumentsList.rejected.type,
          selectOffer.rejected.type,
          editOffer.rejected.type,
          confirmOffer.rejected.type,
          submitAdvData.rejected.type,
          getAdvQuestionnaires.rejected.type,
          getCoherenceQuestionnaireData.rejected.type,
          submitCoherenceQuestionnaire.rejected.type,
          submitIncomeDocumentation.rejected.type,
          submitNextPage.rejected.type,
          submitBeneficiary.rejected.type,
          submitIban.rejected.type,
          deselectInsuranceAction.rejected.type,
        ].includes(action.type), // action.type.endsWith('/rejected')
      errorReducer,
    );

    builder.addMatcher(
      (action) =>
        [
          createOtp.fulfilled.type,
          createOtp.rejected.type,
          verifyOtp.rejected.type,
          createDeal.fulfilled.type,
          getOffers.fulfilled.type,
          getAdvQuestionnaires.fulfilled.type,
          selectOffer.fulfilled.type,
          editOffer.fulfilled.type,
          getCoherenceQuestionnaireData.fulfilled.type,
          submitNextPage.fulfilled.type,
          getDocumentsList.fulfilled.type,
        ].includes(action.type), // action.type.endsWith('/rejected')
      disableLoadingReducer,
    );
  },
});

export const {
  setProgressBar,
  setSidebar,
  setLoading,
  setError,
  setIsTimeModalOpen,
} = layoutSlice.actions;

const layoutReducer = layoutSlice.reducer;
export default layoutReducer;
